import {getAdjustedCdnUrl, getUrlWithoutDomain} from '@aboutyou/utils';
import {styled as linariaStyled} from '@linaria/react';
import React, {FC, MouseEventHandler, useState} from 'react';
import sc, {css} from 'styled-components';
import {COLORS} from '../../../constants/colors';
import {FONT_SIZES, FONT_WEIGHTS} from '../../../constants/typography';
import {useInView} from '../../../hooks/useInView/useInView';
import {ImageType} from '../../../types/Image';
import {LinkType} from '../../../types/Link';
import {Icon} from '../../atoms/Icon/Icon';
import {GenericIconName, IconName} from '../../atoms/Icon/IconName';
import {Text} from '../../atoms/Text/Text';
import {UnstyledLink} from '../../atoms/UnstyledLink/UnstyledLink';
import {BadgeContainer} from '../../molecules/BadgeList';
import {ProductImage} from '../../molecules/ProductImage/ProductImage';
import {BadgesContainer} from '../../molecules/ProductTile/BadgesContainer';

const NOTIFICATION_CENTER_TEST_ID = 'notificationCenter';
export interface NotificationCenterCardProps {
  headline: string;
  body: string;
  image?: ImageType;
  url?: LinkType;
  createdAt?: string;
  title?: string;
  headerIconName: IconName;
  headerIconColor?: string;
  headerBackground?: string;
  badgeList?: React.ReactNode;
  priceBox?: React.ReactNode;
  forceWhiteImageBackground?: boolean;
  info?: {url: string; external: boolean};
  infoButtonTopAlignment?: boolean;
  fallbackIconReplacement?: IconName;
  onInfoClick: MouseEventHandler;
  onCardClick?: VoidFunction;
  preventRedirectOnClick?: boolean;
  progressAnimation?: {
    durationMs: number;
    isPaused: boolean;
  };
}

const breakpoints = [{columns: 12}];
const widths = [120, 220, 360];

export const NotificationCenterCard: FC<NotificationCenterCardProps> = ({
  headline,
  body,
  image,
  url,
  createdAt,
  title,
  headerIconName,
  headerIconColor,
  headerBackground,
  badgeList,
  priceBox,
  forceWhiteImageBackground,
  info,
  infoButtonTopAlignment,
  fallbackIconReplacement,
  onInfoClick,
  onCardClick,
  preventRedirectOnClick,
  progressAnimation,
}) => {
  const {ref, inView} = useInView<HTMLDivElement>({
    triggerOnce: true,
    threshold: 0,
    rootMargin: '200px',
  });

  const [renderFallbackImage, setRenderFallbackImage] = useState(false);

  const onNotificationCardClick = () => {
    onCardClick?.();

    // the redirect URLs within GetNotificationV2 are not localized
    // therefore, we cannot directly use the considerScrollTopAnchor within the card Link
    // as a redirection will occur in between preventing the initial scroll to top
    window?.scrollTo({top: 0});
  };

  return (
    <Wrapper
      as={url && !preventRedirectOnClick ? UnstyledLink : 'div'}
      // if url is not external we want to remove domain from path and searchQuery
      // if domain is left, link component would treat url as external and would open it in new tab which is unwanted behaviour
      to={url && !url.external ? getUrlWithoutDomain(url.url) : url?.url}
      onClick={onNotificationCardClick}
      ref={ref}
      data-testid={`${NOTIFICATION_CENTER_TEST_ID}-card`}
      $addCursorPointer={Boolean(onCardClick)}
    >
      <Header
        data-testid={`${NOTIFICATION_CENTER_TEST_ID}-cardHeader`}
        $backgroundColor={headerBackground}
      >
        <HeaderProgressFill
          progressAnimation={progressAnimation}
          headerBackground={headerBackground}
        />
        <NotificationType>
          <Icon asset={headerIconName} size="XS" fill={headerIconColor} />
          <HeaderText size="px10" color={headerBackground ? 'White' : 'Black'}>
            {headline}
          </HeaderText>
        </NotificationType>
        {createdAt && (
          <HeaderText size="px10" color={headerBackground ? 'White' : 'Black'}>
            {createdAt}
          </HeaderText>
        )}
      </Header>

      <Body>
        <ProductImageSection
          $enableGreyBackground={!image || renderFallbackImage}
          data-testid={`${NOTIFICATION_CENTER_TEST_ID}-cardImage`}
        >
          {image && !renderFallbackImage ? (
            <>
              <StyledProductImage
                padded
                src={
                  forceWhiteImageBackground
                    ? getAdjustedCdnUrl(image.url, {
                        bg: 'FFFFFF',
                        brightness: 1,
                      })
                    : image.url
                }
                alt={image.description}
                breakpoints={breakpoints}
                widths={widths}
                importance={inView ? 'high' : 'low'}
                loading={inView ? 'eager' : 'lazy'}
                onError={() => {
                  setRenderFallbackImage(true);
                }}
              />
              <BadgesWrapper>{badgeList}</BadgesWrapper>
            </>
          ) : (
            <FallbackProductImage
              asset={fallbackIconReplacement || GenericIconName.BellFilled}
              size={fallbackIconReplacement ? 'L' : 'S'}
              fill={headerBackground}
            />
          )}
        </ProductImageSection>
        <InfoSection
          data-testid={`${NOTIFICATION_CENTER_TEST_ID}-cardInfoSection`}
        >
          <InfoSectionTitle size="px14" weight="mediumHeavy">
            {title}
          </InfoSectionTitle>
          <InfoSectionBody size="px12">{body}</InfoSectionBody>
          {priceBox && (
            <PriceSection data-testid="price-box">{priceBox}</PriceSection>
          )}
        </InfoSection>
        {info && (
          <IconSection
            $topAlignment={infoButtonTopAlignment}
            data-testid="heroInfoCondition"
          >
            <StyledIcon
              asset={GenericIconName.InfoFilledGray}
              size={'XS'}
              onClick={onInfoClick}
            />
          </IconSection>
        )}
      </Body>
    </Wrapper>
  );
};

const Wrapper = sc.div<{$addCursorPointer?: boolean}>`
  background-color: ${COLORS.White};
  box-shadow: 0px 4px 15px 0px rgba(0, 0, 0, 0.03);
  border-radius: 8px;
  display: block;
  overflow: hidden;

  ${({$addCursorPointer}) =>
    $addCursorPointer &&
    css`
      cursor: pointer;
    `}
`;

const Header = sc.div<{$backgroundColor?: string}>`
  align-items: center;
  display: flex;
  flex: 1;
  justify-content: space-between;
  border-bottom: 1px solid ${COLORS.Grey4};
  padding: 4px 8px;
  position: relative;
  overflow: hidden;

  ${props =>
    props.$backgroundColor
      ? css`
          background-color: ${props.$backgroundColor};
          color: white;
        `
      : ''}
`;

const HeaderText = sc(Text).attrs({
  size: 'px10',
})`
  line-height: 16px;
  position: relative;
  z-index: 1;
`;

const NotificationType = sc.div`
  display: flex;
  gap: 4px;
  align-items: center;
  position: relative;
  z-index: 1;
`;

const HeaderProgressFill: FC<{
  progressAnimation?: NotificationCenterCardProps['progressAnimation'];
  headerBackground?: string;
}> = ({progressAnimation, headerBackground}) => {
  if (!progressAnimation || !headerBackground) {
    return null;
  }

  return (
    <ProgressFill
      data-testid={`${NOTIFICATION_CENTER_TEST_ID}-progressBar`}
      aria-hidden="true"
      style={{
        ['--shop-hint-progress-bg' as string]: headerBackground,
        animationDuration: `${progressAnimation.durationMs}ms`,
        animationPlayState: progressAnimation.isPaused ? 'paused' : 'running',
      }}
    />
  );
};

const ProgressFill = linariaStyled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  transform: translateX(-100%);

  background-color: var(--shop-hint-progress-bg);
  background-color: color-mix(
    in srgb,
    var(--shop-hint-progress-bg) 90%,
    black
  );

  animation-name: shop-hint-progress;
  animation-timing-function: linear;
  animation-fill-mode: forwards;

  @keyframes shop-hint-progress {
    from { transform: translateX(-100%); }
    to   { transform: translateX(0); }
  }

  @media (prefers-reduced-motion: reduce) {
    animation-duration: 0ms !important;
  }
`;

const Body = sc.div`
  padding: 8px;
  display: flex;
  gap: 16px;
`;

const ProductImageSection = sc.div<{$enableGreyBackground: boolean}>`
  aspect-ratio: 3 / 4;
  height: 102px;
  position: relative;
  border-radius: 4px;

  ${({$enableGreyBackground}) =>
    $enableGreyBackground &&
    css`
      background-color: ${COLORS.ProductImageBackground};
    `}
`;

const StyledProductImage = linariaStyled(ProductImage)`
  height: 102px;
  padding: 0;
  width: 74px;

  &&& {
    & > img {
      border-radius: 4px;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      max-height: 100%;
      max-width: 100%;
    }
  }
`;

const FallbackProductImage = sc(Icon)`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  top: 0;
  margin: auto;
`;

const BadgesWrapper = linariaStyled.div`
  bottom: 0;
  display: flex;
  flex-direction: column;
  left: 0;
  position: absolute;
  width: 100%;

  ${BadgesContainer} {
    bottom: 8px;
  }

  ${BadgeContainer} {
    font-size: ${FONT_SIZES.px10};
    font-weight: ${FONT_WEIGHTS.mediumHeavy};
    height: auto;
    line-height: ${FONT_SIZES.px16};
    padding: 0px 6px 1px 8px;
  }
`;

const InfoSection = sc.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 8px;
  padding: 0 10px 10px 0;
`;

const IconSection = sc.div<{$topAlignment?: boolean}>`
  display: flex;
  justify-content: center;
  margin-right: 10px;
  ${props =>
    props.$topAlignment
      ? css`
          align-items: start;
          padding-top: 10px;
        `
      : '  align-items: center;'}
`;

const StyledIcon = sc(Icon)`
  fill: ${COLORS.Grey2};
`;

const InfoSectionTitle = sc(Text)`
  line-height: 20px;
`;

const InfoSectionBody = sc(Text)`
  line-height: 18px;
`;

const PriceSection = sc.div`
  width: 100%;
`;
